import { useMemo, useState } from 'react';
import { useAuthStore } from '../../stores/auth';
import {
  Heading,
  BodyText,
  Card,
  MetricCard,
  METRIC_READ_CAPTIONS,
  PageHeader,
  Select,
  BarChart,
  Skeleton,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/ucc-filing-analytics';
import {
  useFilingVolume,
  useJurisdictions,
  usePerfectionStatus,
  useRejectionRates,
  useTurnaroundTime,
} from '../../hooks';
import {
  ABSENT,
  formatCount,
  formatDateTime,
  formatRatioAsPercent,
} from '../../utils/display-format';
import {
  metricCellClass,
  metricGridClass,
  MetricSkeletons,
  PORTFOLIO_COUNT_LABEL,
  PORTFOLIO_DETAIL_FIELDS,
  UccTenantScope,
} from './ucc-display';

type FilingVolumeSummary = components['schemas']['FilingVolumeSummary'];
type RejectionRateBreakdown = components['schemas']['RejectionRateBreakdown'];
type JurisdictionTurnaroundTime = components['schemas']['JurisdictionTurnaroundTime'];

/** Rows requested per analytics report; the spec caps `limit` at 100. */
export const ANALYTICS_ROW_LIMIT = 100;

/** How many jurisdictions each chart plots. */
const CHART_JURISDICTIONS = 10;

// ---------------------------------------------------------------------------
// Aggregation over the canonical report rows
// ---------------------------------------------------------------------------

/**
 * Filing volume from the FilingVolumeSummary rows: the sum of filingCount,
 * and per-jurisdiction sums, largest first. The page previously printed
 * `totalCount`, which counts snapshot ROWS (one per jurisdiction, filing type
 * and period), not filings.
 */
export function summarizeVolume(rows: FilingVolumeSummary[]): {
  total: number;
  byJurisdiction: Array<{ jurisdictionCode: string; filingCount: number }>;
} {
  const totals = new Map<string, number>();
  let total = 0;
  for (const row of rows) {
    total += row.filingCount;
    totals.set(row.jurisdictionCode, (totals.get(row.jurisdictionCode) ?? 0) + row.filingCount);
  }
  const byJurisdiction = [...totals.entries()]
    .map(([jurisdictionCode, filingCount]) => ({ jurisdictionCode, filingCount }))
    .sort((a, b) => b.filingCount - a.filingCount);
  return { total, byJurisdiction };
}

/**
 * Rejection rate as sum(rejectedCount) / sum(totalFilings), overall and per
 * jurisdiction. A denominator of zero yields null (no filings to rate), never
 * a measured 0%.
 */
export function summarizeRejections(rows: RejectionRateBreakdown[]): {
  rate: number | null;
  byJurisdiction: Array<{ jurisdictionCode: string; rate: number }>;
} {
  const perJurisdiction = new Map<string, { rejected: number; total: number }>();
  let rejected = 0;
  let total = 0;
  for (const row of rows) {
    rejected += row.rejectedCount;
    total += row.totalFilings;
    const acc = perJurisdiction.get(row.jurisdictionCode) ?? { rejected: 0, total: 0 };
    acc.rejected += row.rejectedCount;
    acc.total += row.totalFilings;
    perJurisdiction.set(row.jurisdictionCode, acc);
  }
  const byJurisdiction = [...perJurisdiction.entries()]
    .filter(([, acc]) => acc.total > 0)
    .map(([jurisdictionCode, acc]) => ({ jurisdictionCode, rate: acc.rejected / acc.total }))
    .sort((a, b) => b.rate - a.rate);
  return { rate: total > 0 ? rejected / total : null, byJurisdiction };
}

/**
 * Mean turnaround in milliseconds, each row's averageTurnaroundMs weighted by
 * its filingCount. Null when no row carries a filing.
 */
export function meanTurnaroundMs(rows: JurisdictionTurnaroundTime[]): number | null {
  let weighted = 0;
  let filings = 0;
  for (const row of rows) {
    weighted += row.averageTurnaroundMs * row.filingCount;
    filings += row.filingCount;
  }
  return filings > 0 ? weighted / filings : null;
}

/** A duration in the largest unit that keeps it readable: "45 minutes", "6.5 hours", "3.2 days". */
export function formatDuration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return ABSENT;
  const minutes = ms / 60_000;
  if (minutes < 60) return `${Math.round(minutes)} minutes`;
  const hours = minutes / 60;
  if (hours < 48) return `${hours.toFixed(1)} hours`;
  return `${(hours / 24).toFixed(1)} days`;
}

/** The caption a metric takes from its read state, or undefined once a figure printed. */
function readCaption(error: Error | null, hasRows: boolean): string | undefined {
  if (error) return METRIC_READ_CAPTIONS.failed;
  if (!hasRows) return METRIC_READ_CAPTIONS.notReported;
  return undefined;
}

function partialNote(received: number, totalCount: number): string | undefined {
  return totalCount > received ? `Sum of ${formatCount(received)} of ${formatCount(totalCount)} snapshot rows` : undefined;
}

const NO_SNAPSHOTS =
  'No analytics snapshots exist for this tenant yet. The analytics service computes these figures from snapshots written by its refresh, and none have been written.';

function ChartPanel({
  title,
  isLoading,
  error,
  data,
  emptyText,
}: {
  title: string;
  isLoading: boolean;
  error: Error | null;
  data: Array<{ label: string; value: number }>;
  emptyText: string;
}) {
  return (
    <Card className="p-6">
      <Heading level={3} size="card" className="mb-4">{title}</Heading>
      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : error ? (
        <BodyText variant="ui" className="text-sm text-error">Could not be read: {error.message}</BodyText>
      ) : data.length > 0 ? (
        <BarChart title={title} data={data} height={250} />
      ) : (
        <BodyText variant="ui" className="text-tertiary text-sm">{emptyText}</BodyText>
      )}
    </Card>
  );
}

export function UCCAnalyticsPage() {
  const tenantId = useAuthStore(s => s.user?.tenantId) ?? '';
  const [jurisdiction, setJurisdiction] = useState('');

  const query = { tenantId, jurisdiction: jurisdiction || undefined, limit: ANALYTICS_ROW_LIMIT };
  const volume = useFilingVolume(query);
  const turnaround = useTurnaroundTime(query);
  const rejection = useRejectionRates(query);
  const perfection = usePerfectionStatus({ tenantId });
  const jurisdictions = useJurisdictions();

  const volumeRows = volume.data?.data ?? [];
  const turnaroundRows = turnaround.data?.data ?? [];
  const rejectionRows = rejection.data?.data ?? [];
  const perfectionSummary = perfection.data?.data?.[0];

  const volumeSummary = useMemo(() => summarizeVolume(volumeRows), [volumeRows]);
  const rejectionSummary = useMemo(() => summarizeRejections(rejectionRows), [rejectionRows]);
  const turnaroundMs = useMemo(() => meanTurnaroundMs(turnaroundRows), [turnaroundRows]);

  // The empty-value entry is a real, re-selectable option; Select omits its
  // disabled placeholder when an option already carries the empty value.
  const jurisdictionOptions = [
    { value: '', label: 'All jurisdictions' },
    ...(jurisdictions.data?.data ?? []).map((j) => ({ value: j.stateCode, label: `${j.stateName} (${j.stateCode})` })),
  ];

  const anyMetricLoading = volume.isLoading || turnaround.isLoading || rejection.isLoading || perfection.isLoading;
  const metricCount = 4;

  const volumeCaption = readCaption(volume.error, volumeRows.length > 0);
  const turnaroundCaption = readCaption(turnaround.error, turnaroundRows.length > 0);
  const rejectionCaption = readCaption(rejection.error, rejectionRows.length > 0);
  const perfectionCaption = readCaption(perfection.error, perfectionSummary !== undefined);

  return (
    <div>
      <PageHeader description="UCC filing volume, turnaround, and rejection metrics" />

      <UccTenantScope tenantId={tenantId} />

      {tenantId && (
        <>
          {/* The filter row sits under the tenant line, where Filings places its
              FilterBar. In the header it bottom-aligned the row to the labeled
              select and dropped the description 39 px below its sibling pages. */}
          <div className="flex flex-wrap items-end gap-4 mb-6">
            <Select
              label="Jurisdiction"
              options={jurisdictionOptions}
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              fullWidth={false}
              helperText={jurisdictions.error ? `Jurisdiction list could not be read: ${jurisdictions.error.message}` : undefined}
            />
          </div>

          <div className={`${metricGridClass(metricCount)} mb-6`}>
            {anyMetricLoading ? (
              <MetricSkeletons count={metricCount} />
            ) : (
              <>
                <MetricCard
                  label="Filing volume"
                  value={volumeCaption ? ABSENT : formatCount(volumeSummary.total)}
                  caption={volumeCaption}
                  description={volumeCaption ? undefined : partialNote(volumeRows.length, volume.data?.totalCount ?? 0)}
                  className={metricCellClass(0, metricCount)}
                />
                <MetricCard
                  label="Average turnaround"
                  value={turnaroundCaption ? ABSENT : formatDuration(turnaroundMs)}
                  caption={turnaroundCaption}
                  description={!turnaroundCaption && turnaroundMs === null ? 'No filings in the snapshot rows' : undefined}
                  className={metricCellClass(1, metricCount)}
                />
                <MetricCard
                  label="Rejection rate"
                  value={rejectionCaption || rejectionSummary.rate === null ? ABSENT : formatRatioAsPercent(rejectionSummary.rate)}
                  caption={rejectionCaption}
                  description={!rejectionCaption && rejectionSummary.rate === null ? 'No filings in the snapshot rows' : undefined}
                  className={metricCellClass(2, metricCount)}
                />
                <MetricCard
                  label="Perfected"
                  value={perfectionCaption || !perfectionSummary ? ABSENT : formatCount(perfectionSummary.perfectedCount)}
                  caption={perfectionCaption}
                  description={perfectionSummary ? `As of ${formatDateTime(perfectionSummary.summaryGeneratedAt)}` : undefined}
                  className={metricCellClass(3, metricCount)}
                />
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <ChartPanel
              title="Filing Volume by Jurisdiction"
              isLoading={volume.isLoading}
              error={volume.error}
              data={volumeSummary.byJurisdiction.slice(0, CHART_JURISDICTIONS).map((j) => ({
                label: j.jurisdictionCode,
                value: j.filingCount,
              }))}
              emptyText={NO_SNAPSHOTS}
            />
            <ChartPanel
              title="Rejection Rate by Jurisdiction (%)"
              isLoading={rejection.isLoading}
              error={rejection.error}
              data={rejectionSummary.byJurisdiction.slice(0, CHART_JURISDICTIONS).map((j) => ({
                label: j.jurisdictionCode,
                value: Number((j.rate * 100).toFixed(1)),
              }))}
              emptyText={NO_SNAPSHOTS}
            />
          </div>

          {/* The same counts, labels and MetricCard the Dashboard and Filings
              pages print, all eight of them. */}
          <section aria-label="Portfolio Perfection">
            <Heading level={3} size="card" className="mb-4">
              Portfolio Perfection
            </Heading>
            {perfection.isLoading ? (
              <div className={metricGridClass(PORTFOLIO_DETAIL_FIELDS.length)}>
                <MetricSkeletons count={PORTFOLIO_DETAIL_FIELDS.length} />
              </div>
            ) : perfection.error ? (
              <BodyText variant="ui" className="text-sm text-error">
                Could not be read: {perfection.error.message}
              </BodyText>
            ) : !perfectionSummary ? (
              <BodyText variant="ui" className="text-sm text-tertiary">
                No portfolio summary has been generated for this tenant yet.
              </BodyText>
            ) : (
              <>
                <div className={metricGridClass(PORTFOLIO_DETAIL_FIELDS.length)}>
                  {PORTFOLIO_DETAIL_FIELDS.map((field, i) => (
                    <MetricCard
                      key={field}
                      label={PORTFOLIO_COUNT_LABEL[field]}
                      value={formatCount(perfectionSummary[field])}
                      className={metricCellClass(i, PORTFOLIO_DETAIL_FIELDS.length)}
                    />
                  ))}
                </div>
                <BodyText variant="ui" className="text-sm text-tertiary">
                  As of {formatDateTime(perfectionSummary.summaryGeneratedAt)}. Perfected counts acknowledged and
                  active filings; pending counts draft and submitted filings.
                </BodyText>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}
